import { format } from 'date-fns';
import { parseJobDate } from '../../utils/newDate';
import { appointmentCalendarYmd, resolveJobTimeZone } from '../OpenJobPage/openJobDate';
import type { StatusEmptyCopy, StatusSurfaceState } from '../StatusPage/statusSurface';
import { statusSurfaceState } from '../StatusPage/statusSurface';

export { statusSurfaceState };
export type { StatusEmptyCopy, StatusSurfaceState };

export const ORDERS_EMPTY: StatusEmptyCopy = {
    eyebrow: 'JOBS · EMPTY',
    headline: 'No assigned jobs yet',
    body:
        'This list is empty after a successful load — Orders does not invent a placeholder job or a 0-job count.',
    cta: 'Create job',
    note:
        'TRUE EMPTY · LIVE — Create job asks to confirm, then Firestore add writes pending assigned to you. This is not CREATE · SOT and not JOBS · ERROR or JOBS · OFFLINE. SoT EMPTY (jobsSot=empty) never writes. Mark available on Status so dispatch can also find you.',
};

export const ORDERS_EMPTY_SOT: StatusEmptyCopy = {
    eyebrow: 'JOBS · EMPTY',
    headline: 'SoT empty — Create job is paint-only',
    body:
        'This is not a true-empty tech. Assigned-jobs snapshot did not run. Create job opens CREATE · SOT. There is no confirm dialog. Firestore add does not run. Live assigned jobs are unchanged.',
    cta: 'Create job',
    note:
        'CREATE · SOT — no confirm, no write. True-empty confirm → Firestore add is a different path. Do not empty a playground tech — use TRUE EMPTY · WALK (?trueEmptySot=walk).',
};

export const ORDERS_TRUE_EMPTY_WALK: StatusEmptyCopy = {
    eyebrow: 'TRUE EMPTY · WALK',
    headline: 'Confirm → Firestore add path (no write)',
    body:
        'This paints a successful zero-job load without emptying a playground tech. Create job shows the live confirm dialog. Firestore add will not run. Assigned jobs stay unchanged.',
    cta: 'Create job',
    note:
        'On a real zero-job tech the same confirm writes pending assigned to you. SoT EMPTY (jobsSot=empty) skips confirm and stays CREATE · SOT.',
};

export const ORDERS_ERROR: StatusEmptyCopy = {
    eyebrow: 'JOBS · ERROR',
    headline: 'Assigned jobs could not load',
    body: 'Your assigned jobs did not load. Orders will not treat that as zero work or hide an active job.',
    cta: 'Retry',
    note: 'Check Firestore rules for your region, then retry.',
};

export const ORDERS_OFFLINE: StatusEmptyCopy = {
    eyebrow: 'JOBS · OFFLINE',
    headline: 'You are offline',
    body:
        'Orders cannot read assigned jobs without a connection. Cached counts stay hidden instead of showing 0.',
    cta: 'Retry',
    note: 'Reconnect, then retry. Create Job stays so you can still start work once you are back online.',
};

export const ORDERS_TAB_EMPTY: Record<'pending' | 'active' | 'complete', StatusEmptyCopy> = {
    pending: {
        eyebrow: 'PENDING · EMPTY',
        headline: 'No pending jobs in this list',
        body: 'Nothing is waiting in Pending. Other tabs may still have assigned jobs. This is not JOBS · EMPTY — that card is only for a tech with zero assigned jobs.',
        cta: 'Create job',
        note: 'This is not JOBS · EMPTY — that card is only for a tech with zero assigned jobs. Check Active or Complete, or create a job.',
    },
    active: {
        eyebrow: 'ACTIVE · EMPTY',
        headline: 'No active jobs in this list',
        body: 'Nothing is in progress. Other tabs may still have assigned jobs. This is not JOBS · EMPTY — that card is only for a tech with zero assigned jobs.',
        cta: 'Create job',
        note: 'This is not JOBS · EMPTY — that card is only for a tech with zero assigned jobs. Check Pending or Complete, or create a job.',
    },
    complete: {
        eyebrow: 'COMPLETE · EMPTY',
        headline: 'No complete jobs in this list',
        body: 'Nothing is finished yet. Other tabs may still have assigned jobs. This is not JOBS · EMPTY — that card is only for a tech with zero assigned jobs.',
        cta: 'Create job',
        note: 'This is not JOBS · EMPTY — that card is only for a tech with zero assigned jobs. Check Pending or Active, or create a job.',
    },
};

export const PWA_JOBS_PREVIEW_VALUES = [
    'empty',
    'error',
    'offline',
    'pending',
    'pending-handoff',
    'handoff',
    'complete-handoff',
] as const;
export const PWA_JOBS_SOT_VALUES = PWA_JOBS_PREVIEW_VALUES;
export const PWA_JOBS_DEV_PATH = '/dev/jobs';
export const PWA_JOBS_PATH = '/app/orders';
export { PWA_JOBS_ALIAS_PATH } from './assignedJobs';

export type PwaJobsFixtureSurface = typeof PWA_JOBS_PREVIEW_VALUES[number];

export const PWA_JOBS_PREVIEW_ERROR = 'Local preview — assigned-jobs snapshot did not run.';

export const PWA_JOBS_PREVIEW_NOTICE =
    'Local preview — empty / error / offline helper cards. Hosting production ignores jobsPreview.';

export const PWA_JOBS_SOT_ERROR = 'PWA SoT fixture — assigned-jobs snapshot did not run.';

export const PWA_JOBS_SOT_NOTICE =
    'PWA SoT fixture — empty / error / offline helper cards. Hosting honors jobsSot for a signed-in tech. Assigned-jobs snapshot did not run. Live assigned jobs stay unchanged without this query. EMPTY Create job is CREATE · SOT (no confirm, no write) — not the true-empty confirm → Firestore add path. PENDING · HANDOFF paints one Demo Camry pending row. HANDOFF · ACTIVE paints the assignJob Active tab without a live write. COMPLETE · HANDOFF paints the completeJob Complete tab without a live write.';

export const PWA_JOBS_PENDING_HANDOFF_NOTICE =
    'PWA SoT fixture — shop / admin Pending handoff. Hosting honors jobsSot=pending-handoff for a signed-in tech. Assigned-jobs snapshot did not run. This paints one Demo Camry pending row the way a live createIntake / createShopJob write appears on /app/orders (or /app/jobs). DEMO WALK current step stays 2 Open a Pending row. This is not PENDING · EMPTY, not leftover jobsSot=handoff Active paint, and not a live write. Live assigned jobs stay unchanged. Never ADAS.';

export const PWA_JOBS_HANDOFF_NOTICE =
    'PWA SoT fixture — assignJob handoff. Hosting honors jobsSot=handoff for a signed-in tech. Assigned-jobs snapshot did not run. This paints Active the way a live assignJob write appears on /app/orders (or /app/jobs). DEMO WALK current step stays 4 Complete via completeJob. Live assigned jobs stay unchanged. Only assignJob writes active. Never ADAS.';

export const PWA_JOBS_COMPLETE_HANDOFF_NOTICE =
    'PWA SoT fixture — completeJob handoff. Hosting honors jobsSot=complete-handoff for a signed-in tech. Assigned-jobs snapshot did not run. This paints Complete the way a live completeJob write appears on /app/orders (or /app/jobs). DEMO WALK current step stays 4 with 1–3 done. Admin Complete follows with a named region and exclusive counts — no bounce to /regions. Live assigned jobs stay unchanged. Only completeJob writes complete. Never ADAS.';

function queryParam(search: string | undefined, key: string): string | null {
    if (!search) return null;
    const raw = String(search).startsWith('?') ? String(search).slice(1) : String(search);
    return new URLSearchParams(raw).get(key);
}

export function pwaJobsPreviewSurface(
    search?: string,
    env: string | undefined = process.env.NODE_ENV,
): PwaJobsFixtureSurface | null {
    if (env === 'production') return null;
    const value = queryParam(search, 'jobsPreview');
    if (value && (PWA_JOBS_PREVIEW_VALUES as readonly string[]).includes(value)) {
        return value as PwaJobsFixtureSurface;
    }
    return null;
}

export function pwaJobsSotSurface(search?: string): PwaJobsFixtureSurface | null {
    const value = queryParam(search, 'jobsSot');
    if (value && (PWA_JOBS_SOT_VALUES as readonly string[]).includes(value)) {
        return value as PwaJobsFixtureSurface;
    }
    return null;
}

/** Hosting honors jobsSot only for a signed-in tech. Local CRA can still use jobsPreview. */
export function pwaJobsSotAllowed(input: { techId?: string | null } = {}): boolean {
    return Boolean(input.techId);
}

export function pwaJobsSotActive(
    search?: string,
    input: { techId?: string | null } = {},
): PwaJobsFixtureSurface | null {
    if (!pwaJobsSotAllowed(input)) return null;
    return pwaJobsSotSurface(search);
}

export const PWA_WALK_STATUS_VALUES = ['pending', 'active', 'complete'] as const;
export type PwaWalkStatus = typeof PWA_WALK_STATUS_VALUES[number];

export const PWA_WALK_SOT_VALUES = ['pending-handoff', 'handoff', 'complete-handoff'] as const;
export type PwaWalkSotSurface = typeof PWA_WALK_SOT_VALUES[number];

export const PWA_PLAYGROUND_REGION_ID = 'ip6fSKveWZJOloXjEFeA';
export const PWA_PREVIEW_TEST_REGION_ID = 'preview-test-region';
export const PWA_PLAYGROUND_REGION_LABEL = 'colorado2';
export const PWA_TEST_REGION_LABEL = 'Test Region';

export type PwaRegionOption = { id: string; label: string };

function searchParams(search = ''): URLSearchParams {
    const raw = String(search).startsWith('?') ? String(search).slice(1) : String(search);
    return new URLSearchParams(raw);
}

function hrefFromParams(params: URLSearchParams): string {
    const qs = params.toString();
    return qs ? `${PWA_JOBS_PATH}?${qs}` : PWA_JOBS_PATH;
}

export function isPwaWalkStatus(value: unknown): value is PwaWalkStatus {
    return value === 'pending' || value === 'active' || value === 'complete';
}

export function isPwaWalkSot(value: unknown): value is PwaWalkSotSurface {
    return value === 'pending-handoff' || value === 'handoff' || value === 'complete-handoff';
}

/** True when the PWA jobs query already names a walk chip. */
export function pwaSearchHasStatus(search = ''): boolean {
    return isPwaWalkStatus(searchParams(search).get('status'));
}

export function pwaStatusFromSearch(search = ''): PwaWalkStatus | null {
    const value = searchParams(search).get('status');
    return isPwaWalkStatus(value) ? value : null;
}

export function pwaSearchWithStatus(search = '', status: PwaWalkStatus | 'all' | null): string {
    const params = searchParams(search);
    if (!status || status === 'all') params.delete('status');
    else params.set('status', status);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

/** Pending fixtures that stay on Open Pending / Pending-tab entry. Mirrors admin scoreboardLiveJobsEntryHref. */
export const PWA_PENDING_ENTRY_JOBS_SOT = ['pending', 'pending-seed', 'pending-handoff'] as const;

function isPwaPendingEntryJobsSot(value: string | null): boolean {
    return Boolean(value && (PWA_PENDING_ENTRY_JOBS_SOT as readonly string[]).includes(value));
}

/**
 * Pending tab / DEMO WALK Open a Pending row.
 * Mirrors admin `scoreboardLiveJobsEntryHref`: keep leftover pending-seed /
 * pending-handoff / pending. Drop Active / Complete jobsSot (handoff /
 * complete-handoff) so Pending is not wearing Active / Complete. After
 * unify, write ?status=pending so entry does not fall to Seed.
 */
export function pwaLiveJobsEntryHref(search = ''): string {
    const params = searchParams(search);
    const sot = params.get('jobsSot');
    if (sot && !isPwaPendingEntryJobsSot(sot)) params.delete('jobsSot');
    const qs = params.toString();
    return `${PWA_JOBS_PATH}${pwaSearchWithStatus(qs ? `?${qs}` : '', 'pending')}`;
}

/**
 * Walk chip implied by a Hosting-safe jobsSot when ?status= is missing.
 * After unify, DEMO WALK fill follows ?status= — do not leave tabs on Seed/Pending.
 */
export function pwaWalkStatusFromSot(search = ''): PwaWalkStatus | null {
    const sot = pwaJobsSotSurface(search);
    if (sot === 'handoff') return 'active';
    if (sot === 'complete-handoff') return 'complete';
    if (sot === 'pending-handoff' || sot === 'pending') return 'pending';
    return null;
}

export function pwaJobsSotHref(surface?: string, search = ''): string {
    if (!surface || !(PWA_JOBS_SOT_VALUES as readonly string[]).includes(surface)) return PWA_JOBS_PATH;
    const existing = searchParams(search);
    existing.delete('jobsSot');
    existing.delete('status');
    const params = new URLSearchParams();
    const fromSot = pwaWalkStatusFromSot(`?jobsSot=${surface}`);
    if (fromSot) params.set('status', fromSot);
    params.set('jobsSot', surface);
    existing.forEach((value, key) => params.set(key, value));
    return hrefFromParams(params);
}

/** List href for a jobsSot surface — walk fixtures keep their chip so back / switch stay coherent. */
export function pwaJobsSotListHref(surface?: string | null, search = ''): string {
    if (!surface || !(PWA_JOBS_SOT_VALUES as readonly string[]).includes(surface)) return PWA_JOBS_PATH;
    return pwaJobsSotHref(surface, search);
}

/** Hosting / CRA fixture query keys Exit SoT must drop. */
export const PWA_SOT_QUERY_KEYS = [
    'jobsSot',
    'createSot',
    'jobsPreview',
    'createPreview',
    'trueEmptySot',
    'trueEmptyPreview',
] as const;

export function pwaSearchWithoutSot(search = ''): string {
    const params = searchParams(search);
    PWA_SOT_QUERY_KEYS.forEach((key) => params.delete(key));
    const sot = pwaJobsSotSurface(search);
    if (sot && (PWA_WALK_SOT_VALUES as readonly string[]).includes(sot)) {
        params.delete('status');
        params.delete('region');
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

/** Exit jobsSot / createSot / previews → live assigned list. No leftover walk query. */
export function pwaJobsSotExitHref(search = ''): string {
    return `${PWA_JOBS_PATH}${pwaSearchWithoutSot(search)}`;
}

export function isPwaTestRegionName(name: string): boolean {
    return name.trim().toLowerCase() === PWA_TEST_REGION_LABEL.toLowerCase();
}

export function pwaRegionFromSearch(search = ''): string {
    return (searchParams(search).get('region') || '').trim();
}

export function pwaRegionLabel(regionId: string, options: readonly PwaRegionOption[] = []): string {
    const id = regionId.trim();
    if (id === PWA_PLAYGROUND_REGION_ID) return PWA_PLAYGROUND_REGION_LABEL;
    if (id === PWA_PREVIEW_TEST_REGION_ID) return PWA_TEST_REGION_LABEL;
    const found = options.find((option) => option.id === id);
    if (found?.label) {
        if (isPwaTestRegionName(found.label)) return PWA_TEST_REGION_LABEL;
        return found.label;
    }
    return id || PWA_PLAYGROUND_REGION_LABEL;
}

/** Hosting-safe colorado2 ↔ Test Region while a walk jobsSot is on. Never invents ADAS ids. */
export function pwaRegionSwitcherOptions(regionId = ''): PwaRegionOption[] {
    const options: PwaRegionOption[] = [
        { id: PWA_PLAYGROUND_REGION_ID, label: PWA_PLAYGROUND_REGION_LABEL },
        { id: PWA_PREVIEW_TEST_REGION_ID, label: PWA_TEST_REGION_LABEL },
    ];
    const id = regionId.trim();
    if (id && !options.some((option) => option.id === id)) {
        return [{ id, label: pwaRegionLabel(id) }, ...options];
    }
    return options;
}

export function pwaWalkRegionId(search = '', claimRegionId = ''): string {
    const fromQuery = pwaRegionFromSearch(search);
    if (fromQuery) return fromQuery;
    const claim = claimRegionId.trim();
    if (claim === PWA_PLAYGROUND_REGION_ID || claim === PWA_PREVIEW_TEST_REGION_ID) return claim;
    return PWA_PLAYGROUND_REGION_ID;
}

export const PWA_LIVE_WRITE_REGION_ERROR =
    'preview-test-region is a Hosting fixture label, not a Firestore region. Live completeJob and assigned-job creates use the region document named Test Region from tech claims (regionId). This client will not invent that id.';

/** Fixture label is never a live write target. Accepts a region id or `/regions/{id}` prefix. */
export function pwaLiveWriteRegionError(regionOrPrefix?: string | null): string | null {
    const id = String(regionOrPrefix || '').trim().replace(/^\/regions\//, '').split('/')[0];
    if (id === PWA_PREVIEW_TEST_REGION_ID) return PWA_LIVE_WRITE_REGION_ERROR;
    return null;
}

/**
 * Fixture walks keep the Hosting switcher (colorado2 ↔ preview-test-region).
 * Live pages follow tech claims and do not adopt preview-test-region from the query.
 */
export function pwaDisplayRegionId(search = '', claimRegionId = '', fixtureWalk = false): string {
    if (fixtureWalk) return pwaWalkRegionId(search, claimRegionId);
    const claim = claimRegionId.trim();
    if (claim) return claim;
    return PWA_PLAYGROUND_REGION_ID;
}

/**
 * Mid-DEMO WALK region switch keeps ?status= / jobsSot. From a jobsSot-only
 * URL, writes the matching chip so colorado2 ↔ Test Region does not fall to
 * Seed/Pending. Never uses Exit SoT.
 */
export function pwaRegionWalkHref(regionId: string, search = '', jobStatus?: string): string {
    const params = searchParams(search);
    const id = regionId.trim() || PWA_PLAYGROUND_REGION_ID;
    params.set('region', id);
    if (isPwaWalkStatus(jobStatus)) {
        params.set('status', jobStatus);
    } else if (!pwaSearchHasStatus(search)) {
        const fromSot = pwaWalkStatusFromSot(search);
        if (fromSot) params.set('status', fromSot);
    }
    return hrefFromParams(params);
}

export function pwaRegionWalkHint(regionLabel: string, regionId: string): string {
    const label = regionLabel.trim() || 'this region';
    const id = regionId.trim() || 'region';
    return `${label} · ${id}. Switch rebinds DEMO WALK to this region. ?status= and jobsSot stay.`;
}

export function pwaWalkStatusHref(search = '', status: PwaWalkStatus): string {
    if (status === 'pending') return pwaLiveJobsEntryHref(search);
    return `${PWA_JOBS_PATH}${pwaSearchWithStatus(search, status)}`;
}

/** Prefer Hosting-safe jobsSot, then CRA-only jobsPreview. */
export function pwaJobsFixtureSurface(
    search?: string,
    input: { techId?: string | null } = {},
    env: string | undefined = process.env.NODE_ENV,
): PwaJobsFixtureSurface | null {
    const sot = pwaJobsSotActive(search, input);
    if (sot) return sot;
    return pwaJobsPreviewSurface(search, env);
}

export function pwaJobsFixtureNotice(
    search?: string,
    input: { techId?: string | null } = {},
    env: string | undefined = process.env.NODE_ENV,
): string | null {
    const sot = pwaJobsSotActive(search, input);
    if (sot === 'complete-handoff') return PWA_JOBS_COMPLETE_HANDOFF_NOTICE;
    if (sot === 'handoff') return PWA_JOBS_HANDOFF_NOTICE;
    if (sot === 'pending-handoff') return PWA_JOBS_PENDING_HANDOFF_NOTICE;
    if (sot) return PWA_JOBS_SOT_NOTICE;
    const preview = pwaJobsPreviewSurface(search, env);
    if (preview === 'complete-handoff') return PWA_JOBS_COMPLETE_HANDOFF_NOTICE;
    if (preview === 'handoff') return PWA_JOBS_HANDOFF_NOTICE;
    if (preview === 'pending-handoff') return PWA_JOBS_PENDING_HANDOFF_NOTICE;
    if (preview) return PWA_JOBS_PREVIEW_NOTICE;
    return null;
}

export function pwaJobsFixtureError(kind?: string): string {
    return kind === 'sot' ? PWA_JOBS_SOT_ERROR : PWA_JOBS_PREVIEW_ERROR;
}

export function pwaJobsPreviewJobs(surface?: string): Array<Record<string, unknown>> {
    if (surface === 'complete-handoff') {
        return [
            {
                id: 'sot-complete-handoff-1',
                status: 'complete',
                date: '2026-09-12 16:00',
                type: 'Install for Front',
                vehicle: { year: 2018, make: 'Toyota', model: 'Camry' },
            },
        ];
    }
    if (surface === 'handoff') {
        return [
            {
                id: 'sot-handoff-1',
                status: 'active',
                date: '2026-09-12 14:00',
                type: 'Install for Front',
                vehicle: { year: 2018, make: 'Toyota', model: 'Camry' },
            },
        ];
    }
    if (surface === 'pending-handoff') {
        return [
            {
                id: 'sot-pending-handoff-1',
                status: 'pending',
                date: '2026-09-12 14:00',
                type: 'Install for Front',
                vehicle: { year: 2018, make: 'Toyota', model: 'Camry' },
            },
        ];
    }
    if (surface !== 'pending') return [];
    return [
        {
            id: 'preview-complete-1',
            status: 'complete',
            date: '2026-09-11 10:00',
            type: 'Install for Front',
            vehicle: { year: 2018, make: 'Toyota', model: 'Camry' },
        },
    ];
}

export function ordersListCopy(
    surface: StatusSurfaceState,
    emptyKind?: string | null,
): StatusEmptyCopy | null {
    if (surface === 'offline') return ORDERS_OFFLINE;
    if (surface === 'error') return ORDERS_ERROR;
    if (surface === 'empty') {
        if (emptyKind === 'true-empty') return ORDERS_TRUE_EMPTY_WALK;
        if (emptyKind === 'sot' || emptyKind === 'preview') return ORDERS_EMPTY_SOT;
        return ORDERS_EMPTY;
    }
    return null;
}

export function ordersEmptyCopyKind({
    sot,
    preview,
    trueEmptyWalk,
}: {
    sot?: string | null;
    preview?: string | null;
    trueEmptyWalk?: string | null;
} = {}): string | null {
    if (trueEmptyWalk) return 'true-empty';
    if (sot === 'empty') return 'sot';
    if (preview === 'empty') return 'preview';
    return null;
}

export function ordersFilterEmptyCopy(loadedCount: number): string {
    return `No assigned jobs match this date in the ${loadedCount} loaded.`;
}

export function ordersFilterEmptyCard(loadedCount: number): StatusEmptyCopy {
    return {
        eyebrow: 'JOBS · FILTER',
        headline: 'No jobs on this date',
        body: ordersFilterEmptyCopy(loadedCount),
        cta: 'Clear date',
        note: 'Clear the date filter to see every assigned job.',
    };
}

/** Header must not treat a failed, pending, or date-filter-empty load as “no pending jobs”. */
export function ordersHeaderDetail(input: {
    surface: StatusSurfaceState;
    pendingCount: number;
    activeCount?: number;
    completeCount?: number;
    loadedCount: number;
    filterEmpty?: boolean;
}): string | null {
    if (input.surface !== 'ready') return null;
    if (input.filterEmpty) return null;
    if (input.pendingCount > 0) return `Showing ${input.pendingCount} pending job(s)`;
    if ((input.activeCount || 0) > 0) return `Showing ${input.activeCount} active job(s)`;
    if ((input.completeCount || 0) > 0) return `Showing ${input.completeCount} complete job(s)`;
    if (input.loadedCount > 0) return 'No pending jobs in this list';
    return null;
}

/** Epoch millis for a job appointment. Missing, blank, and unparseable times are NaN, not 0. */
export function ordersJobTime(dateValue: unknown): number {
    try {
        const time = parseJobDate(dateValue)?.getTime();
        return typeof time === 'number' && !Number.isNaN(time) ? time : Number.NaN;
    } catch {
        return Number.NaN;
    }
}

function ordersJobDateValue(job: unknown): unknown {
    if (!job || typeof job !== 'object') return undefined;
    return (job as { date?: unknown }).date;
}

/**
 * Orders date sort. `olderFirst` is the header branch where sortingOrder === 'descending'
 * (the existing sign flip: smaller times first). Otherwise newer jobs come first.
 * Missing, blank, and unparseable times sort last in both directions and tie with
 * each other so their relative order stays stable. Dated jobs keep the previous difference.
 */
export function compareOrdersJobsByDate(a: unknown, b: unknown, olderFirst: boolean): number {
    const aTime = ordersJobTime(ordersJobDateValue(a));
    const bTime = ordersJobTime(ordersJobDateValue(b));
    const aInvalid = Number.isNaN(aTime);
    const bInvalid = Number.isNaN(bTime);
    if (aInvalid && bInvalid) return 0;
    if (aInvalid) return 1;
    if (bInvalid) return -1;
    return olderFirst ? aTime - bTime : bTime - aTime;
}

const LEGACY_APPOINTMENT = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;

type AppointmentFields = { date?: unknown; timezone?: unknown; timeZone?: unknown };

function appointmentFromJob(value: unknown): AppointmentFields | null {
    if (!value || typeof value !== 'object' || value instanceof Date) return null;
    if (!Object.prototype.hasOwnProperty.call(value, 'date')) return null;
    const record = value as AppointmentFields;
    return { date: record.date, timezone: record.timezone, timeZone: record.timeZone };
}

function resolveOrdersAppointment(jobOrDate: unknown, timezone?: unknown, timeZone?: unknown): AppointmentFields {
    const job = appointmentFromJob(jobOrDate);
    if (!job) return { date: jobOrDate, timezone, timeZone };
    return {
        date: job.date,
        timezone: timezone ?? job.timezone,
        timeZone: timeZone ?? job.timeZone,
    };
}

function zonedDayKey(date: unknown, timezone?: unknown, timeZone?: unknown): string | null {
    const ymd = appointmentCalendarYmd(date, timezone, timeZone);
    if (!ymd) return null;
    const month = String(ymd.month).padStart(2, '0');
    const day = String(ymd.day).padStart(2, '0');
    return `${month}/${day}/${ymd.year}`;
}

function zonedClock(date: Date, timezone?: unknown, timeZone?: unknown): string | null {
    if (Number.isNaN(date.getTime())) return null;
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: resolveJobTimeZone(timezone, timeZone),
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }).formatToParts(date);
        const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? '';
        const minute = part('minute');
        const dayPeriod = part('dayPeriod').replace(/\./g, '').toUpperCase();
        const hour = Number(part('hour'));
        if (!minute || !dayPeriod || !Number.isFinite(hour)) return null;
        return `${hour}:${minute} ${dayPeriod}`;
    } catch {
        return null;
    }
}

function zonedDateTimeLabel(instant: Date, timezone?: unknown, timeZone?: unknown): string | null {
    const day = zonedDayKey(instant, timezone, timeZone);
    const clock = zonedClock(instant, timezone, timeZone);
    if (!day || !clock) return null;
    return `${day} ${clock}`;
}

function legacyDayKey(value: string): string | null {
    const match = value.trim().match(LEGACY_APPOINTMENT);
    if (!match) return null;
    return `${match[2]}/${match[3]}/${match[1]}`;
}

function legacyDateTimeLabel(value: string): string | null {
    const match = value.trim().match(LEGACY_APPOINTMENT);
    if (!match) return null;
    const hour24 = Number(match[4]);
    const suffix = hour24 >= 12 ? 'PM' : 'AM';
    let hour = hour24 % 12;
    if (hour === 0) hour = 12;
    return `${match[2]}/${match[3]}/${match[1]} ${hour}:${match[5]} ${suffix}`;
}

function serializedTimestamp(value: string): Date | null {
    const trimmed = value.trim();
    const looksTimestamp =
        (trimmed.startsWith('{') && trimmed.endsWith('}')) || /timestamp|_seconds|nanoseconds/i.test(trimmed);
    if (!looksTimestamp) return null;
    return parseJobDate(trimmed);
}

/**
 * Group key `MM/dd/yyyy`. An ISO instant or a timestamp is the calendar day in
 * `job.timezone`, then `job.timeZone`, defaulting to America/Denver, the same day
 * `openJobDateLabel` prints. A legacy `yyyy-MM-dd HH:mm` value stays on its prefix day.
 * Pass the job so the zone is visible; a raw date still defaults to America/Denver.
 */
export function ordersJobDateKey(jobOrDate: unknown, timezone?: unknown, timeZone?: unknown): string {
    try {
        const appointment = resolveOrdersAppointment(jobOrDate, timezone, timeZone);
        const { date } = appointment;
        if (typeof date === 'string') {
            const value = date.trim();
            if (!value) return 'Unknown date';
            const zoned = zonedDayKey(value, appointment.timezone, appointment.timeZone);
            if (zoned) return zoned;
            const legacy = legacyDayKey(value);
            if (legacy) return legacy;
            const stamp = serializedTimestamp(value);
            if (stamp) return zonedDayKey(stamp, appointment.timezone, appointment.timeZone) ?? 'Unknown date';
            const parsed = parseJobDate(value);
            return parsed ? format(parsed, 'MM/dd/yyyy') : 'Unknown date';
        }
        return zonedDayKey(date, appointment.timezone, appointment.timeZone) ?? 'Unknown date';
    } catch {
        return 'Unknown date';
    }
}

/** `MM/dd/yyyy h:mm a` for the same zone rules as `ordersJobDateKey`. */
export function ordersJobDateTimeLabel(jobOrDate: unknown, timezone?: unknown, timeZone?: unknown): string {
    try {
        const appointment = resolveOrdersAppointment(jobOrDate, timezone, timeZone);
        const { date } = appointment;
        if (typeof date === 'string') {
            const value = date.trim();
            if (!value) return 'Unknown date';
            if (zonedDayKey(value, appointment.timezone, appointment.timeZone)) {
                const instant = new Date(value);
                return zonedDateTimeLabel(instant, appointment.timezone, appointment.timeZone) ?? 'Unknown date';
            }
            const legacy = legacyDateTimeLabel(value);
            if (legacy) return legacy;
            const stamp = serializedTimestamp(value);
            if (stamp) return zonedDateTimeLabel(stamp, appointment.timezone, appointment.timeZone) ?? 'Unknown date';
            const parsed = parseJobDate(value);
            return parsed ? format(parsed, 'MM/dd/yyyy h:mm a') : 'Unknown date';
        }
        const parsed = parseJobDate(date);
        if (!parsed) return 'Unknown date';
        return zonedDateTimeLabel(parsed, appointment.timezone, appointment.timeZone) ?? 'Unknown date';
    } catch {
        return 'Unknown date';
    }
}

type OrdersFilterJob = { date?: unknown; timezone?: unknown; timeZone?: unknown };

/**
 * True when the appointment is an ISO instant or a Timestamp-like value that
 * already has a zoned calendar day. Legacy `yyyy-MM-dd HH:mm` strings do not.
 */
function ordersDateHasZoneLabel(job: OrdersFilterJob): boolean {
    const appointment = resolveOrdersAppointment(job);
    return zonedDayKey(appointment.date, appointment.timezone, appointment.timeZone) != null;
}

/**
 * Date-filter match. A zoned appointment matches the group key (`MM/dd/yyyy`)
 * and the zoned date-time label only — the raw ISO text is not searched, so a
 * Denver evening does not match its UTC day. Legacy strings still match the
 * group key and the stored text.
 */
export function ordersJobMatchesFilter(job: OrdersFilterJob, filterTerm?: string): boolean {
    const term = (filterTerm || '').trim().toLowerCase();
    if (!term) return true;
    const key = ordersJobDateKey(job).toLowerCase();
    if (ordersDateHasZoneLabel(job)) {
        const label = ordersJobDateTimeLabel(job).toLowerCase();
        return key.includes(term) || label.includes(term);
    }
    const raw = typeof job?.date === 'string' ? job.date.toLowerCase() : '';
    return key.includes(term) || raw.includes(term);
}
